import { useEffect, useRef, useState } from 'react';
import useGeneralSubscription from './useGeneralSubscription';
import { SUBSCRIPTION_STATUS_TYPE } from '../constants/benchat';
import benchatService from '../service/benchatService';
import useCountdownTimer from './useCountdownTimer';

export const DEFAULT_SECONDS = 5;

/**
 * For platforms that need to jump to a third party, resulting in inability to obtain subscription results immediately, such as FB, Line
 * @typedef {object} ReturnValue
 * @property {boolean} hasSubscribed
 * @property {function} startPolling
 * @property {string} subscriptionStatus
 * @property {object} userRef
 * @property {function} unsubscribe
 * @property {object} currentSubscriptions
 *
 * @param {object} props
 * @param {string} props.platform
 * @param {array} props.topics
 * @param {array} props.subscriptions
 * @param {string} props.userId
 * @param {number=DEFAULT_SECONDS} props.pollingSeconds
 * @returns {ReturnValue}
 */
const useAsyncSubscription = (props) => {
  const { hasSubscribed, setHasSubscribed, unsubscribe, checkMemberReward } =
    useGeneralSubscription(props);
  const { hasFinished, isRunning, timeLeft, reset, start } = useCountdownTimer({
    defaultSeconds: props.pollingSeconds ?? DEFAULT_SECONDS,
  });
  const [currentSubscriptions, setCurrentSubscriptions] = useState(
    props.subscriptions,
  );
  const [subscriptionStatus, setSubscriptionStatus] = useState(
    SUBSCRIPTION_STATUS_TYPE.INITIAL,
  );
  const userRef = useRef(benchatService.generateUserRef(props.userId));

  const fetchAndUpdateState = async () => {
    try {
      if (hasFinished) {
        stopPolling();
        setSubscriptionStatus(SUBSCRIPTION_STATUS_TYPE.NO_RECORD);
        return;
      }
      const response = await benchatService.getSubscriptions(
        props.userId,
        props.topics,
        props.platform,
      );

      const responseSubscriptionStatus = benchatService.getSubscribeStatus(
        response.data.subscriptions,
        userRef.current,
      );
      if (responseSubscriptionStatus === SUBSCRIPTION_STATUS_TYPE.LOADING) {
        return;
      }

      stopPolling();
      setCurrentSubscriptions(response.data.subscriptions);
      setSubscriptionStatus(responseSubscriptionStatus);
      setHasSubscribed(
        responseSubscriptionStatus === SUBSCRIPTION_STATUS_TYPE.SUCCESS,
      );
    } catch (error) {
      console.error(error);
    }
  };

  const startPolling = () => {
    setSubscriptionStatus(SUBSCRIPTION_STATUS_TYPE.LOADING);
    start();
  };

  const stopPolling = () => {
    userRef.current = benchatService.generateUserRef(props.userId);
    reset();
  };

  const unsubscribeAndUpdateUserRef = async () => {
    const result = await unsubscribe();
    if (result === SUBSCRIPTION_STATUS_TYPE.SUCCESS) {
      userRef.current = benchatService.generateUserRef(props.userId);
    }
    return result;
  };

  useEffect(() => {
    if (isRunning) {
      fetchAndUpdateState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft, isRunning]);

  return {
    userRef,
    currentSubscriptions,
    subscriptionStatus,
    hasSubscribed,
    startPolling,
    unsubscribe: unsubscribeAndUpdateUserRef,
    checkMemberReward,
  };
};

useAsyncSubscription.propTypes = useGeneralSubscription.propTypes;

export default useAsyncSubscription;
